import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isEmpty } from "lodash";
import { Card, EmptyLayout, SearchBar } from "../../components/molecules";
import classNames from "classnames";
import {
  menuDeleteIcon,
  menuEditIcon,
  appLightIcon
} from "../../assets/icons";
import { DRAWERS } from "../../components/organisms/AppDrawers/drawers.constants";
import { actions as drawerActions } from "../../components/organisms/Drawer/drawers.reducers";
import {
  selectMuleIntegrations,
  deleteMuleIntegration,
  fetchMuleIntegrations,
} from "./index";
import Spinner, { SPINNER_NAMES } from "../../components/organisms/Spinner";
import DeleteConfirmationModal from "../../components/organisms/AppModals/DeleteConfirmationModal";
import { actions as modalActions } from "../../components/organisms/Modal/modal.reducers";
import { POPOVER_BUTTONS } from "../../constants";
import { MODALS } from "../../components/organisms/AppModals/modals.constants";
import { actions as appActions } from "../Applications/applications.reducers";
import { actions as mulesoftActions } from "./mulesoft.reducers";
import { actions as breadCrumbActions } from "../../components/organisms/BreadCrumb/breadCrumb.reducers";

const { CREATE_INTEGRATION_DRAWER } = DRAWERS;
const { setSelectedDrawer } = drawerActions;
const { FETCH_MULESOFT_INTEGRATIONS } = SPINNER_NAMES;
const { setSelectedModal, setItemToBeDeleted } = modalActions;
const { DELETE, EDIT } = POPOVER_BUTTONS;
const { DELETE_MODAL } = MODALS;
const { setShouldShowMenuBar } = appActions;
const {
  setSelectedIntegration,
  setSelectedIntegrationId,
  setMuleIntegrationsFilters,
} = mulesoftActions;
const { setSelectedTab } = breadCrumbActions;

const MuleIntegrations = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data: integrations, total: totalMuleIntegrations } = useSelector(selectMuleIntegrations);
  const [shouldShowDeleteModal, setShouldShowDeleteModal] = useState(false);
  const currentPath = window.location.pathname;

  useEffect(() => {
    dispatch(setShouldShowMenuBar(false));
  }, [dispatch, currentPath]);

  const handleShouldShowDrawer = useCallback(
    () => dispatch(setSelectedDrawer(CREATE_INTEGRATION_DRAWER)),
    [dispatch]
  );

  const popOverOptions = (integration) => [
    {
      name: EDIT,
      icon: menuEditIcon,
      classes: "border-t border-gray-600",
      handleOnClick: () => {
        dispatch(setSelectedIntegration(integration));
        dispatch(setSelectedDrawer(CREATE_INTEGRATION_DRAWER));
      },
    },
    {
      name: DELETE,
      icon: menuDeleteIcon,
      classes: "border-t border-gray-600",
      handleOnClick: () => {
        dispatch(setSelectedModal(DELETE_MODAL));
        dispatch(setItemToBeDeleted(integration));
        setShouldShowDeleteModal(true);
      },
    },
  ];

  const handleDeleteDataModel = useCallback(
    (integrationToBeDeleted) => {
      dispatch(deleteMuleIntegration(integrationToBeDeleted.id)).then((res) => {
        if (res.payload) {
          dispatch(setSelectedModal(null));
          setShouldShowDeleteModal(false);
          dispatch(fetchMuleIntegrations());
        }
      });
    },
    [dispatch]
  );

  const handleOnSearch = useCallback(
    (value) => dispatch(setMuleIntegrationsFilters({ name: value })),
    [dispatch]
  );

  useEffect(() => {
    dispatch(setSelectedTab(null));
    dispatch(setSelectedIntegration(null));
    dispatch(setSelectedIntegrationId(null));
    dispatch(setMuleIntegrationsFilters());
  }, [dispatch]);

  return (
    <div className="scrollbar content overflow-auto p-6 flex flex-1 flex-col">
      <Helmet>
        <title>Mulesoft Integrations | TavaJS</title>
      </Helmet>
      <div className={classNames("flex-col md:flex-row flex mb-4 gap-4 justify-end", { "justify-between": !!totalMuleIntegrations })}>
        {!!totalMuleIntegrations && (
          <SearchBar placeholder="Search" handleOnSearch={handleOnSearch} />
        )}
        <div className=" flex flex-col md:flex-row gap-4">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleShouldShowDrawer}
          >
            Create New Integration
          </button>
        </div>
      </div>
      <div className="justify-between flex flex-col flex-1">
        <Spinner name={FETCH_MULESOFT_INTEGRATIONS}>
          {!isEmpty(integrations) ? (
            <>
              <div className="grid grid-cols-12 gap-4 w-full">
                {integrations.map((integration) => (
                  <Card
                    key={integration.id}
                    name={integration.name}
                    createdAt={integration.createdAt}
                    popOverOptions={popOverOptions(integration)}
                    onClick={() => {
                      navigate(`/mulesoft/${integration.id}/endpoints`);
                      dispatch(setSelectedIntegration(integration));
                    }}
                  />
                ))}
              </div>
              {shouldShowDeleteModal && (
                <DeleteConfirmationModal
                  handleDelete={handleDeleteDataModel}
                  setShouldShowDeleteModal={setShouldShowDeleteModal}
                  title="Delete Integration"
                />
              )}
            </>
          ) : (
            <EmptyLayout
              title="No Integrations"
              subTitle="You can get started by creating a new Integration."
              buttonText="Create New Integration"
              image={appLightIcon}
              handleOnClick={handleShouldShowDrawer}
            />
          )}
        </Spinner>
      </div>
    </div>
  );
};
export default MuleIntegrations;
